<template>
    <div v-if="widgetReady">
        <template v-if="!hasCustomContent && hasPendingChange">
            <button class="btn changes-button" type="button" @click="toggleCollapse">
                Show Upcoming Changes <i :class="{'fa-angle-up': isCollapsed, 'fa-angle-down': !isCollapsed}" class="fas wl-color fa-lg"></i>
            </button>
            <div v-show="isCollapsed" class="upcoming-changes-panel">
                <h5 v-if="hasFutureDiscounts()" class="discount-changes-heading">Pending Discount Changes Effective {{ discountEffectiveDate | shortDate }}</h5>
                <template v-if="!noFuturePlan">
                    <plan-changes style="margin-left:0px; margin-right: 0px;":currentPlan="currentPlan" :futurePlan="futurePlan"></plan-changes>
                </template>
                <div v-if="hasFuturePriceTier() && showPricing">
                    <h5 class="pending-price-changes-heading">Pending Price Changes</h5>
                    <div v-for="(row, idx) in pendingPriceRows" :key="pendingPriceRowKey(row, idx)" class="pending-price-row">
                        <div class="pending-price-row-main">
                            <template v-if="row.kind === 'edit'">
                                <span class="pending-price-badge">Future edit</span>
                                <span class="pending-price-meta">
                                    Ages {{ row.tier.futureSettings.minAge }} – {{ row.tier.futureSettings.maxAge }}
                                    <span class="pending-price-dot" aria-hidden="true">·</span>
                                    {{ row.tier.relationship }}
                                </span>
                                <span class="pending-price-sep" aria-hidden="true">·</span>
                                <span class="pending-price-effective">
                                    <i class="far fa-calendar-alt" aria-hidden="true"></i>
                                    <span>Begins {{ row.tier.futureSettings.effectiveDate | shortDate }}</span>
                                </span>
                            </template>
                            <template v-else-if="row.kind === 'delete'">
                                <span class="pending-price-badge pending-price-badge--muted">Tier ending</span>
                                <span class="pending-price-meta">
                                    Ages {{ row.tier.minAge }} – {{ row.tier.maxAge }}
                                    <span class="pending-price-dot" aria-hidden="true">·</span>
                                    {{ row.tier.relationship }}
                                </span>
                                <template v-if="row.tier.endDate">
                                    <span class="pending-price-sep" aria-hidden="true">·</span>
                                    <span class="pending-price-effective pending-price-effective--warn">
                                        <i class="far fa-calendar-times" aria-hidden="true"></i>
                                        <span>Ends {{ row.tier.endDate | shortDate }}</span>
                                    </span>
                                </template>
                            </template>
                            <template v-else-if="row.kind === 'future'">
                                <span class="pending-price-badge pending-price-badge--new">
                                    {{ row.tier.effectiveDate ? 'New price' : 'Price change' }}
                                </span>
                                <span class="pending-price-meta">
                                    Ages {{ row.tier.minAge }} – {{ row.tier.maxAge }}
                                    <span class="pending-price-dot" aria-hidden="true">·</span>
                                    {{ row.tier.relationship }}
                                </span>
                                <template v-if="row.tier.effectiveDate">
                                    <span class="pending-price-sep" aria-hidden="true">·</span>
                                    <span class="pending-price-effective">
                                        <i class="far fa-calendar-alt" aria-hidden="true"></i>
                                        <span>Begins {{ row.tier.effectiveDate | shortDate }}</span>
                                    </span>
                                </template>
                            </template>
                        </div>
                        <span class="pending-price-value">
                            <template v-if="row.kind === 'edit'">{{ row.tier.futureSettings.price | currency }}</template>
                            <template v-else>{{ row.tier.price | currency }}</template>
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>



<script>
import { api } from "../../../global/framework/api.js";
import { mapGetters } from 'vuex';
import PlanChanges from "./PlanChanges.vue";
import { common } from "../../../global/common.js";

export default {
    name: "UpcomingPlanChanges",
    components: { PlanChanges },
    props: { planId: { type: [Number], required: true } },
    data() {
        return {
            widgetReady: false,
            currentPlan: {},
            futurePlan: {},
            futureDiscounts: [],
            noFuturePlan: false,
            discountEffectiveDate: "",
            currentTiers: [],
            futureOnlyPriceTiers: [],
            deletedOnlyPriceTiers: [],
            isCollapsed: false
        };
    },
    computed: {
        currentTiersWithFutureEdits() {
            return this.currentTiers.filter(tier => tier.futureSettings != null);
        },
        pendingPriceRows() {
            const rows = [];
            this.currentTiersWithFutureEdits.forEach(tier => rows.push({ kind: 'edit', tier }));
            this.deletedOnlyPriceTiers.forEach(tier => rows.push({ kind: 'delete', tier }));
            this.futureOnlyPriceTiers.forEach(tier => rows.push({ kind: 'future', tier }));
            return rows;
        },
        hasPendingChange() {
            const hasFuturePlan = this.hasFuturePlan();
            const hasFuturePriceTier = this.hasFuturePriceTier();
            const hasFutureDiscounts = this.hasFutureDiscounts();

            if (hasFuturePlan) {
                return true;
            }

            if (hasFuturePriceTier || hasFutureDiscounts) {
                this.noFuturePlan = !hasFuturePlan;
                return true;
            }

            return false;
        },
        hasCustomContent() {
            return Boolean(this.$scopedSlots.default);
        },
        ...mapGetters({ showPricing: 'globalSettings/showPricing' })
    },
    methods: {
        pendingPriceRowKey(row, index) {
            const id = row.tier && row.tier.id != null ? row.tier.id : index;
            return `${row.kind}-${id}`;
        },
        toggleCollapse() {
            this.isCollapsed = !this.isCollapsed;
        },
        hasFutureDiscounts() {
            return this.futureDiscounts.length > 0;
        },
        hasFuturePlan() {
            return Boolean(this.futurePlan) && Boolean(this.futurePlan.planId);
        },
        hasFuturePriceTier() {
            return this.tierHasChange(this.futureOnlyPriceTiers) || this.tierHasChange(this.currentTiers) || this.tierHasChange(this.deletedOnlyPriceTiers);
        },
        tierHasChange(tiers) {
            return tiers.some(tier => tier.futureSettings != null || tier.endDate != null);
        }
    },
    async mounted() {
        try {
            let [current, future, futureDiscounts] = await Promise.all([
                api.getData('Shared', this.planId, 'GetCurrentPlanSettingsView'),
                api.getData('Shared', this.planId, 'GetFuturePlanSettingsView'),
                api.getData('Shared', this.planId, 'GetFutureDiscounts')
            ]);

            this.currentPlan = current;
            this.futurePlan = future;
            this.futureDiscounts = futureDiscounts;

            if (this.hasFutureDiscounts()) {
                this.discountEffectiveDate = this.futureDiscounts[0].effectiveDate;
            }

            let data = await api.get('Shared', this.planId, 'GetPriceTiers');

            this.currentTiers = data.current.map(tier => ({ ...tier, relationship: common.RELATIONSHIP_TYPE[tier.consumerRelationshipId] }));
            this.futureOnlyPriceTiers = data.futureOnly.map(tier => ({ ...tier, relationship: common.RELATIONSHIP_TYPE[tier.consumerRelationshipId] }));
            this.deletedOnlyPriceTiers = data.deletedOnly.map(tier => ({ ...tier, relationship: common.RELATIONSHIP_TYPE[tier.consumerRelationshipId] }));

            this.widgetReady = true;
        } catch (error) {
            console.error('Error fetching plan data:', error);
        }
    }
};


</script>

<style scoped>
    /* Self-contained panel (BS3 has no .card/.card-body; BS4+ card was invisible on PSR) */
    .upcoming-changes-panel {
        margin-top: 8px;
        padding: 16px 18px;
        background-color: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        box-sizing: border-box;
    }

    .changes-button:focus {
        outline: 0 !important;
        box-shadow: none;
    }

    .changes-button {
        margin: 5px 0;
        padding: 8px 0 8px 0;
        color: var(--bs-success, #198754) !important;
        font-weight: bold !important;
        font-size: 1.05rem;
        background: transparent;
        border: none;
        cursor: pointer;
    }

    .changes-button i {
        padding-left: 5px;
    }

    .discount-changes-heading {
        font-size: 1rem;
        font-weight: 700;
        margin: 0 0 12px;
        color: #212529;
    }

    .pending-price-changes-heading {
        font-size: 1rem;
        font-weight: 700;
        margin: 0 0 4px;
        padding-bottom: 10px;
        border-bottom: 2px solid var(--bs-success, #198754);
        color: #212529;
    }

    .pending-price-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px 16px;
        padding: 10px 0;
        border-bottom: 1px solid #e9ecef;
    }

    .pending-price-row:last-child {
        border-bottom: none;
        padding-bottom: 2px;
    }

    .pending-price-row-main {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        column-gap: 8px;
        row-gap: 4px;
        min-width: 0;
    }

    .pending-price-badge {
        display: inline-block;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #495057;
        padding: 4px 10px;
        border-radius: 4px;
        border: 1px solid #ced4da;
        background: #fff;
    }

    .pending-price-badge--new {
        color: var(--bs-success, #198754);
        border-color: rgba(25, 135, 84, 0.35);
        background: rgba(25, 135, 84, 0.06);
    }

    .pending-price-badge--muted {
        color: #6c757d;
        border-color: #dee2e6;
        background: #f8f9fa;
    }

    .pending-price-value {
        font-size: 1.15rem;
        font-weight: 700;
        color: #212529;
        white-space: nowrap;
    }

    .pending-price-meta {
        font-size: 0.9rem;
        color: #495057;
        line-height: 1.4;
    }

    .pending-price-dot {
        margin: 0 5px;
        color: #adb5bd;
    }

    .pending-price-sep {
        color: #adb5bd;
        user-select: none;
    }

    .pending-price-effective {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
        white-space: nowrap;
    }

    .pending-price-effective i {
        color: var(--bs-success, #198754);
        opacity: 0.9;
    }

    .pending-price-effective--warn i {
        color: #b45309;
    }
</style>

<style>
    /* PlanChanges sits inside the panel; slotted content is not scoped to UpcomingPlanChanges */
    .upcoming-changes-panel h5 {
        font-size: 1.05rem;
        font-weight: 600;
        margin: 10px 0 8px;
        color: #212529;
        line-height: 1.35;
    }

    .upcoming-changes-panel h5 .highlight {
        font-size: inherit;
    }
</style>